/**
 * Envio de modelo aprovado pela Meta — o único jeito de falar com a cliente
 * depois de 24h sem mensagem dela.
 */
import type { ConversaResumo, ModeloMensagem } from "@ydchat/shared";
import { renderizarModelo } from "@ydchat/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, LayoutTemplate } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Botao } from "@/components/ui/botao";
import { Campo } from "@/components/ui/campo";
import { Esqueleto } from "@/components/ui/esqueleto";
import { PainelLateral } from "@/components/ui/painel-lateral";
import { Selo } from "@/components/ui/selo";
import { api, ErroApi } from "@/lib/api";
import { useModelos } from "@/lib/central";
import { useUsuario } from "@/lib/sessao";

export function PainelModelos({
  conversa,
  aberto,
  onFechar,
}: {
  conversa: ConversaResumo;
  aberto: boolean;
  onFechar: () => void;
}) {
  const modelos = useModelos(conversa.id, aberto);
  const [escolhido, setEscolhido] = useState<ModeloMensagem | null>(null);

  const fechar = () => {
    setEscolhido(null);
    onFechar();
  };

  return (
    <PainelLateral
      aberto={aberto}
      onFechar={fechar}
      titulo={escolhido ? "Preencher modelo" : "Modelos aprovados"}
    >
      {escolhido ? (
        <FormModelo
          conversa={conversa}
          modelo={escolhido}
          onVoltar={() => setEscolhido(null)}
          onEnviado={fechar}
        />
      ) : modelos.isPending ? (
        <div className="space-y-2">
          <Esqueleto className="h-20" />
          <Esqueleto className="h-20" />
        </div>
      ) : modelos.isError ? (
        <p className="rounded-md bg-danger-100 p-3 text-sm text-danger-500">
          {modelos.error.message}
        </p>
      ) : !modelos.data?.length ? (
        <p className="text-sm text-neutral-500">
          Nenhum modelo aprovado. Crie e aprove modelos no WhatsApp Manager da Meta.
        </p>
      ) : (
        <ul className="space-y-2">
          <li className="pb-1 text-sm text-neutral-600">
            Fora da janela de 24h a Meta só permite modelos aprovados. Quando a cliente responder, a
            conversa volta ao normal.
          </li>
          {[...modelos.data]
            .sort((a, b) => Number(b.suportado) - Number(a.suportado))
            .map((m) => (
              <li key={`${m.nome}-${m.idioma}`}>
                <button
                  disabled={!m.suportado}
                  onClick={() => setEscolhido(m)}
                  className="w-full rounded-lg border border-neutral-200 p-3 text-left hover:border-gold-300 hover:bg-gold-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <span className="flex items-center gap-2 text-sm font-semibold text-neutral-900">
                    <LayoutTemplate size={15} strokeWidth={1.75} /> {m.nome}
                    {!m.suportado && <Selo>Ainda não suportado</Selo>}
                  </span>
                  <span className="mt-1 line-clamp-3 block text-[13px] text-neutral-600">
                    {m.corpo}
                  </span>
                </button>
              </li>
            ))}
        </ul>
      )}
    </PainelLateral>
  );
}

function FormModelo({
  conversa,
  modelo,
  onVoltar,
  onEnviado,
}: {
  conversa: ConversaResumo;
  modelo: ModeloMensagem;
  onVoltar: () => void;
  onEnviado: () => void;
}) {
  const eu = useUsuario();
  const qc = useQueryClient();
  // Sugestão: {{1}} costuma ser o nome da cliente; {{2}}, quem atende.
  const [params, setParams] = useState<string[]>(() =>
    Array.from({ length: modelo.variaveis }, (_, i) =>
      i === 0
        ? (conversa.contato.nome.split(" ")[0] ?? "")
        : i === 1
          ? (eu.nome.split(" ")[0] ?? "")
          : "",
    ),
  );
  const enviar = useMutation({
    mutationFn: () =>
      api.post(`/conversas/${conversa.id}/modelos`, {
        idCliente: crypto.randomUUID(),
        nome: modelo.nome,
        idioma: modelo.idioma,
        parametros: params,
      }),
    onSuccess: () => {
      toast.success("Modelo enviado!");
      void qc.invalidateQueries({ queryKey: ["conversa", conversa.id] });
      void qc.invalidateQueries({ queryKey: ["conversas"] });
      onEnviado();
    },
    onError: (e) =>
      toast.error(e instanceof ErroApi ? e.message : "Não foi possível enviar o modelo."),
  });
  const completo = params.every((p) => p.trim());

  return (
    <div className="space-y-4">
      <button
        onClick={onVoltar}
        className="flex items-center gap-1 text-sm text-neutral-600 hover:text-neutral-900"
      >
        <ArrowLeft size={15} strokeWidth={1.75} /> Outros modelos
      </button>
      {params.map((p, i) => (
        <label key={i} className="block space-y-1.5">
          <span className="block text-sm font-semibold text-neutral-800">
            Variável {`{{${i + 1}}}`}
          </span>
          <Campo
            value={p}
            onChange={(e) =>
              setParams((atual) => atual.map((x, j) => (j === i ? e.target.value : x)))
            }
          />
        </label>
      ))}
      <div>
        <p className="mb-1.5 text-sm font-semibold text-neutral-800">Como a cliente vai receber</p>
        <div className="rounded-xl bg-gold-100 p-3 text-[14.5px] text-neutral-900 shadow-xs">
          {modelo.cabecalho && <p className="mb-1 font-bold">{modelo.cabecalho}</p>}
          <p className="whitespace-pre-wrap">
            {renderizarModelo(
              modelo.corpo,
              params.map((p) => p || "…"),
            )}
          </p>
          {modelo.rodape && <p className="mt-1 text-xs text-neutral-500">{modelo.rodape}</p>}
        </div>
      </div>
      <Botao
        className="w-full justify-center"
        onClick={() => enviar.mutate()}
        disabled={!completo || enviar.isPending}
      >
        Enviar modelo
      </Botao>
    </div>
  );
}
